'use client';
import { Breadcrumb } from '@/components/common/breadcrumb';
import { PageHeader } from '@/components/common/page-header';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  CloseReportResponse,
  PetrohqApiResponseCloseReportResponse,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function CloseStat() {
  const { toast } = useToast();

  // 상태 관리
  const [closeData, setCloseData] = useState<CloseReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 마감 데이터 가져오기 함수
  const fetchCloseData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    if (!filters.closeDate || !filters.closeCount || filters.store.length !== 1) {
      return;
    }

    setIsLoading(true);
    try {
      const storeCode = filters.store[0];
      const response = await apiClient.get('close', {
        searchParams: {
          storeCode,
          closeDate: filters.closeDate,
          closeCnt: filters.closeCount,
        },
      });
      const data =
        (await response.json()) as PetrohqApiResponseCloseReportResponse;

      if (data.result) {
        setCloseData(data.result);
      }
    } catch (error) {
      console.error('Close data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '마감현황 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // 검색 핸들러
  const handleSearch = () => {
    fetchCloseData();
  };

  // 필터 변경 감지는 제거 (온디맨드 방식)

  return (
    <div>
      {/* 아래 카드들이 각자 p-3 을 갖고 있어 여기서는 위·좌우 여백만 준다 */}
      <div className='flex items-center justify-between px-3 pt-3'>
        <Breadcrumb items={menuBreadcrumbItems('close-stat')} />
        <PageHeader onSearch={handleSearch} isLoading={isLoading} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2'>
        {/* 판매집계 그리드 */}
        <div>
          <ExpandableCard title='판매집계' onExpandChange={() => { }}>
            <div className='h-80'>
              <RealGridReact
                gridProps={{
                  display: { fitStyle: RealGrid.GridFitStyle.FILL },
                  checkBar: { visible: false },
                  stateBar: { visible: false },
                  edit: { readOnly: true, editable: false },
                  footer: { visible: false },
                }}
                rows={closeData?.closeSales || []}
              >
                <RGDataField
                  fieldName={'gubun'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'gubunName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'gassale'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'oilsale'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'totsale'}
                  dataType={RealGrid.ValueType.NUMBER}
                />

                <RGDataColumn
                  fieldName={'gubunName'}
                  header={{ text: '구분' }}
                />
                <RGDataColumn
                  fieldName={'gassale'}
                  header={{ text: '일반유' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'oilsale'}
                  header={{ text: '윤활유' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'totsale'}
                  header={{ text: '합계' }}
                  numberFormat='#,##0'
                />
              </RealGridReact>
            </div>
          </ExpandableCard>

          {/* 신용카드 그리드 */}
          <ExpandableCard title='신용카드' onExpandChange={() => { }}>
            <div className='h-72'>
              <RealGridReact
                gridProps={{
                  display: { fitStyle: RealGrid.GridFitStyle.FILL },
                  checkBar: { visible: false },
                  stateBar: { visible: false },
                  edit: { readOnly: true, editable: false },
                  footer: { visible: true },
                }}
                rows={closeData?.closeCreditCard || []}
              >
                <RGDataField
                  fieldName={'code'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'name'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'pcnt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'pamt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'dcnt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'damt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />

                <RGDataColumn
                  fieldName={'code'}
                  header={{ text: '코드' }}
                  width={70}
                />
                <RGDataColumn
                  fieldName={'name'}
                  header={{ text: '카드사명' }}
                />
                <RGDataColumn
                  fieldName={'pcnt'}
                  header={{ text: '건수' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'pamt'}
                  header={{ text: '금액' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'dcnt'}
                  header={{ text: '건수' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'damt'}
                  header={{ text: '금액' }}
                  numberFormat='#,##0'
                />
              </RealGridReact>
            </div>
          </ExpandableCard>
        </div>

        {/* 마감구분별, 주유계기량 그리드 */}
        <div>
          <ExpandableCard title='마감구분별' onExpandChange={() => { }}>
            <div className='h-44'>
              <RealGridReact
                gridProps={{
                  display: { fitStyle: RealGrid.GridFitStyle.FILL },
                  checkBar: { visible: false },
                  stateBar: { visible: false },
                  edit: { readOnly: true, editable: false },
                  footer: { visible: false },
                }}
                rows={closeData?.closeSaleType || []}
              >
                <RGDataField
                  fieldName={'gubun'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'cnt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'amt'}
                  dataType={RealGrid.ValueType.NUMBER}
                />

                <RGDataColumn fieldName={'gubun'} header={{ text: '구분' }} />
                <RGDataColumn
                  fieldName={'cnt'}
                  header={{ text: '건수' }}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'amt'}
                  header={{ text: '금액' }}
                  numberFormat='#,##0'
                />
              </RealGridReact>
            </div>
          </ExpandableCard>

          <ExpandableCard title='주유계기량' onExpandChange={() => { }}>
            <div className='h-[420px]'>
              <RealGridReact
                gridProps={{
                  display: { fitStyle: RealGrid.GridFitStyle.FILL },
                  checkBar: { visible: false },
                  stateBar: { visible: false },
                  edit: { readOnly: true, editable: false },
                  footer: { visible: true },
                  grouping: { enabled: true },
                }}
                rows={closeData?.closeOilGauge || []}
              >
                <RGDataField
                  fieldName={'pumpNo'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'oilCode'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'oilName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'junil'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'today'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'tocir'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'topos'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'toout'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'toerr'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'pNozzle'}
                  dataType={RealGrid.ValueType.TEXT}
                />

                <RGDataColumn
                  fieldName={'pumpNo'}
                  header={{ text: 'No.' }}
                  width={30}
                />
                <RGDataColumn
                  fieldName={'oilName'}
                  header={{ text: '유종' }}
                  width={60}
                />
                <RGDataColumn
                  fieldName={'junil'}
                  header={{ text: '전일계기' }}
                  numberFormat='#,##0.00'
                />
                <RGDataColumn
                  fieldName={'today'}
                  header={{ text: '당일계기' }}
                  numberFormat='#,##0.00'
                />
                <RGDataColumn
                  fieldName={'tocir'}
                  header={{ text: '회전량' }}
                  numberFormat='#,##0.00'
                />
                <RGDataColumn
                  fieldName={'topos'}
                  header={{ text: 'POS' }}
                  numberFormat='#,##0.00'
                />
                <RGDataColumn
                  fieldName={'toerr'}
                  header={{ text: '오차량' }}
                  numberFormat='#,##0.00'
                />
              </RealGridReact>
            </div>
          </ExpandableCard>
        </div>
      </div>

      {/* 유종별 계기 판매표 그리드 */}
      <div>
        <ExpandableCard title='유종별 계기 판매표' onExpandChange={() => { }}>
          <div className='h-48'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
              }}
              rows={closeData?.closeOilSell || []}
            >
              <RGDataField
                fieldName={'oilCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'machsale'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'npsale'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'bqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'jaqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'ntaxqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'movqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'nulqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'scost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'samt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sqty2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'scost2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'samt2'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'machsale'}
                header={{ text: '계기판매(+)' }}
                numberFormat='#,##0.000'
              />
              <RGDataColumn
                fieldName={'npsale'}
                header={{ text: '계기외판매(배달+)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'bqty'}
                header={{ text: '보관판매(-)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'jaqty'}
                header={{ text: '자가사용(-)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'ntaxqty'}
                header={{ text: '면세판매(-)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'movqty'}
                header={{ text: '탱크이관(-)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'nulqty'}
                header={{ text: '공회전(-)' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'sqty'}
                header={{ text: '수량' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'scost'}
                header={{ text: '단가' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'samt'}
                header={{ text: '금액' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'sqty2'}
                header={{ text: '수량' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'scost2'}
                header={{ text: '단가' }}
                numberFormat='#,##0.00'
              />
              <RGDataColumn
                fieldName={'samt2'}
                header={{ text: '금액' }}
                numberFormat='#,##0.00'
              />
            </RealGridReact>
          </div>
        </ExpandableCard>
      </div>

      {/* 유종별 재고 현황 그리드 */}
      <div>
        <ExpandableCard title='유종별 재고 현황' onExpandChange={() => { }}>
          <div className='h-36'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
              }}
              rows={closeData?.closeOilStock || []}
            >
              <RGDataField
                fieldName={'oilCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qtyBefore'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'qtyIn'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'qtyOut'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'outnpqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tojanqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'dm'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tosilqty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'bfTank'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'inTank'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'outTank'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'janTank'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'qtyBefore'}
                header={{ text: '전재고' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'qtyIn'}
                header={{ text: '입고' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'qtyOut'}
                header={{ text: '계기' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'outnpqty'}
                header={{ text: '계기외' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tojanqty'}
                header={{ text: '현재고' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'dm'}
                header={{ text: '드럼' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tosilqty'}
                header={{ text: '실재고' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'bfTank'}
                header={{ text: '전잔량' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'inTank'}
                header={{ text: 'IN' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'outTank'}
                header={{ text: 'OUT' }}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'janTank'}
                header={{ text: '잔량' }}
                numberFormat='#,##0'
              />
            </RealGridReact>
          </div>
        </ExpandableCard>
      </div>
    </div>
  );
}
